/**
 * @fileoverview Local AI Test Runner
 *
 * Runs integration tests locally with AI-generated moves instead of
 * human-specified notation. Uses the same board positions from existing
 * test suites but lets the AI pick moves dynamically.
 *
 * Validation is behavioral — did the AI successfully generate and execute
 * a valid move? — rather than exact FEN comparison.
 */

import { ZodiacEngine } from "../zodiacEngine/zodiacEngine";
import { Mode, State } from "../zodiacEngine/types";
import { AIOrchestrator } from "../zodiacEngine/AI/Core/AIOrchestrator";
import { AIPlayerType } from "../zodiacEngine/AI/Core/IAIPlayer";
import {
  AIModel,
  TimeFrame,
  getBaseAITypeFromModel,
  getTimeLimitFromFrame,
  AI_MODEL_CONFIG,
} from "../zodiacEngine/AI/Core/AIVariants";
import { MinimaxAI } from "../zodiacEngine/AI/MinimaxAI";
import { ZodiacFEN } from "../zodiacEngine/Fen/ZodiacFEN";
import { IntegrationTest } from "./types";
import { TestStep } from "./LocalTestRunner";
import { AITestConfig, AITestResult, AIValidationCheck } from "./AITestTypes";
import { executeAIMove } from "./AIMoveExecutor";

/**
 * Run a single AI test locally
 */
export async function runAITest(
  test: IntegrationTest,
  config: AITestConfig
): Promise<AITestResult> {
  const startTime = performance.now();
  const engine = new ZodiacEngine();

  const steps: TestStep[] = [
    { name: "Initialize Engine", status: "pending" },
    { name: "Setup FEN on Board", status: "pending" },
    { name: "Set Kings", status: "pending" },
    { name: "Configure AI", status: "pending" },
    { name: "Request AI Move", status: "pending" },
    { name: "Execute Move", status: "pending" },
    { name: "Validate Result", status: "pending" },
  ];

  const updateStep = (
    index: number,
    status: TestStep["status"],
    message?: string
  ) => {
    steps[index].status = status;
    steps[index].timestamp = performance.now();
    if (message) steps[index].message = message;
  };

  const createResult = (
    passed: boolean,
    extras?: Partial<AITestResult>
  ): AITestResult => ({
    _type: "ai-local",
    test,
    passed,
    duration: performance.now() - startTime,
    aiMoveMade: "",
    aiThinkingTimeMs: 0,
    aiConfidence: 0,
    beforeFEN: "",
    afterFEN: "",
    validationChecks: [],
    steps,
    ...extras,
  });

  try {
    // Step 1: Initialize engine
    updateStep(0, "running");
    engine.setGameMode(Mode.TestAI);
    updateStep(0, "passed", "Engine initialized in TestAI mode");

    // Step 2: Setup board from FEN
    updateStep(1, "running");
    engine.loadFromFEN(test.inputFEN);
    const beforeFEN = ZodiacFEN.encode(engine.getGameState());
    updateStep(1, "passed", `Loaded: ${beforeFEN}`);

    // Step 3: Set kings and force into playing state
    updateStep(2, "running");
    const gameState = (engine as any).gameStateManager.getGameState();
    if (test.lightKing) gameState.lightKing = test.lightKing;
    if (test.darkKing) gameState.darkKing = test.darkKing;
    gameState.state = State.Playing;
    gameState.turnCount = 10;
    gameState.mode = Mode.TestAI;
    // Set whose turn it is based on config side
    gameState.isLightTurn = config.side === "light";
    updateStep(
      2,
      "passed",
      `Light: ${test.lightKing}, Dark: ${test.darkKing}, Turn: ${config.side}`
    );

    // Step 4: Configure AI
    updateStep(3, "running");
    const aiOrchestrator = new AIOrchestrator(engine);
    const aiType = getBaseAITypeFromModel(config.aiModel);

    if (config.side === "light") {
      aiOrchestrator.setLightPlayer(aiType);
      aiOrchestrator.setDarkPlayer(AIPlayerType.PLAYER);
    } else {
      aiOrchestrator.setDarkPlayer(aiType);
      aiOrchestrator.setLightPlayer(AIPlayerType.PLAYER);
    }

    // Configure minimax model if needed
    const modelConfig = AI_MODEL_CONFIG[config.aiModel];
    if (modelConfig.minimaxModel) {
      const minimaxAI = aiOrchestrator.getAI(
        AIPlayerType.MINIMAX
      ) as MinimaxAI;
      if (minimaxAI) {
        minimaxAI.setModel(modelConfig.minimaxModel);
      }
    }

    updateStep(
      3,
      "passed",
      `AI: ${modelConfig.displayName} (${aiType}) as ${config.side}`
    );

    // Step 5: Request AI move
    updateStep(4, "running");
    const timeLimit = getTimeLimitFromFrame(config.timeFrame);
    const currentState = engine.getGameState();
    const aiThinkStart = performance.now();
    const moveResult = await aiOrchestrator.requestAIMove(
      currentState,
      timeLimit
    );
    const aiThinkingTimeMs = performance.now() - aiThinkStart;

    if (!moveResult) {
      updateStep(4, "failed", "AI returned no move");
      for (let i = 5; i < steps.length; i++) updateStep(i, "skipped");
      return createResult(false, {
        error: "AI returned no move",
        beforeFEN,
        validationChecks: [
          {
            name: "ai_returned_move",
            passed: false,
            details: "AI returned null",
          },
        ],
      });
    }

    updateStep(
      4,
      "passed",
      `AI chose move in ${Math.round(aiThinkingTimeMs)}ms (confidence: ${moveResult.confidence.toFixed(2)})`
    );

    // Capture piece count before move
    const beforePieceCount = countPieces(currentState.board);

    // Step 6: Execute move using BotGameController pattern
    updateStep(5, "running");
    const execResult = await executeAIMove(engine, moveResult, currentState);

    if (!execResult.success) {
      updateStep(5, "failed", execResult.error);
      updateStep(6, "skipped");
      return createResult(false, {
        error: execResult.error,
        beforeFEN,
        aiMoveMade: execResult.moveDescription,
        aiThinkingTimeMs,
        aiConfidence: moveResult.confidence,
        aiReasoning: moveResult.reasoning,
        validationChecks: [
          { name: "ai_returned_move", passed: true },
          {
            name: "move_executed",
            passed: false,
            details: execResult.error,
          },
        ],
      });
    }

    updateStep(5, "passed", execResult.moveDescription);

    // Step 7: Validate result
    updateStep(6, "running");
    const afterState = engine.getGameState();
    const afterFEN = ZodiacFEN.encode(afterState);
    const validationChecks = runValidationChecks(
      currentState,
      afterState,
      beforeFEN,
      afterFEN,
      beforePieceCount,
      config.side,
      timeLimit,
      aiThinkingTimeMs
    );

    const allPassed = validationChecks.every((c) => c.passed);
    const failedChecks = validationChecks.filter((c) => !c.passed);

    if (allPassed) {
      updateStep(6, "passed", `All ${validationChecks.length} checks passed`);
    } else {
      updateStep(
        6,
        "failed",
        `${failedChecks.length} check(s) failed: ${failedChecks.map((c) => c.name).join(", ")}`
      );
    }

    return createResult(allPassed, {
      error: allPassed
        ? undefined
        : `Validation failed: ${failedChecks.map((c) => `${c.name}: ${c.details}`).join("; ")}`,
      beforeFEN,
      afterFEN,
      aiMoveMade: execResult.moveDescription,
      aiThinkingTimeMs,
      aiConfidence: moveResult.confidence,
      aiReasoning: moveResult.reasoning,
      validationChecks,
    });
  } catch (error) {
    steps.forEach((step, i) => {
      if (step.status === "pending" || step.status === "running") {
        updateStep(
          i,
          "failed",
          error instanceof Error ? error.message : String(error)
        );
      }
    });

    return createResult(false, {
      error: error instanceof Error ? error.message : String(error),
      validationChecks: [
        {
          name: "no_crash",
          passed: false,
          details: error instanceof Error ? error.message : String(error),
        },
      ],
    });
  }
}

/**
 * Run all validation checks on the result of an AI move
 */
function runValidationChecks(
  beforeState: any,
  afterState: any,
  beforeFEN: string,
  afterFEN: string,
  beforePieceCount: number,
  side: "light" | "dark",
  timeLimit: number,
  thinkingTime: number
): AIValidationCheck[] {
  const checks: AIValidationCheck[] = [];

  // Check 1: AI returned a move (already validated upstream, always true here)
  checks.push({ name: "ai_returned_move", passed: true });

  // Check 2: Move executed (already validated upstream, always true here)
  checks.push({ name: "move_executed", passed: true });

  // Check 3: Board integrity - still 8 rows, 5 cols
  const boardRows = afterState.board?.length ?? 0;
  const boardCols = afterState.board?.[0]?.length ?? 0;
  checks.push({
    name: "board_integrity",
    passed: boardRows === 8 && boardCols === 5,
    details:
      boardRows === 8 && boardCols === 5
        ? `${boardRows}x${boardCols}`
        : `Expected 8x5, got ${boardRows}x${boardCols}`,
  });

  // Check 4: Piece count valid (no duplication - count should decrease or stay same)
  const afterPieceCount = countPieces(afterState.board);
  checks.push({
    name: "piece_count_valid",
    passed: afterPieceCount <= beforePieceCount,
    details: `Before: ${beforePieceCount}, After: ${afterPieceCount}`,
  });

  // Check 5: Turn changed (unless bonus turn or game over)
  const gameEnded =
    afterState.state === State.PostGame ||
    afterState.state === State.Draw ||
    afterState.state === State.Terminated ||
    afterState.lightWon !== undefined;
  const isBonusTurn =
    afterState.skipTurnChange || afterState.activeBonusTurnPieceId;
  const turnChanged = beforeState.isLightTurn !== afterState.isLightTurn;

  if (gameEnded || isBonusTurn) {
    checks.push({
      name: "turn_changed",
      passed: true,
      details: gameEnded
        ? "Game ended (turn change N/A)"
        : "Bonus turn active",
    });
  } else {
    checks.push({
      name: "turn_changed",
      passed: turnChanged,
      details: turnChanged
        ? `Turn changed from ${side}`
        : `Turn did NOT change (expected change)`,
    });
  }

  // Check 6: State is valid
  const validStates = [
    State.Playing,
    State.PostGame,
    State.Draw,
    State.Terminated,
  ];
  const stateValid = validStates.includes(afterState.state);
  checks.push({
    name: "state_valid",
    passed: stateValid,
    details: `State: ${afterState.state}`,
  });

  // Check 7: King integrity - both kings still exist (unless kingslayer victory)
  const lightKingExists = findKingOnBoard(afterState.board, true);
  const darkKingExists = findKingOnBoard(afterState.board, false);
  const isKingslayerVictory =
    gameEnded &&
    (afterState.lightWon === true || afterState.lightWon === false);

  if (isKingslayerVictory) {
    // One king may be captured — that's valid
    checks.push({
      name: "king_integrity",
      passed: true,
      details: "Kingslayer victory (captured king is expected)",
    });
  } else {
    checks.push({
      name: "king_integrity",
      passed: lightKingExists && darkKingExists,
      details: `Light king: ${lightKingExists ? "present" : "MISSING"}, Dark king: ${darkKingExists ? "present" : "MISSING"}`,
    });
  }

  // Check 8: AI responded within time limit (with generous buffer)
  const timingBuffer = 5000; // 5s buffer for overhead
  checks.push({
    name: "ai_timing",
    passed: thinkingTime <= timeLimit + timingBuffer,
    details: `${Math.round(thinkingTime)}ms (limit: ${timeLimit}ms)`,
  });

  return checks;
}

/**
 * Count total pieces on board
 */
function countPieces(board: any[][]): number {
  let count = 0;
  for (const row of board) {
    for (const square of row) {
      count += square.Pieces?.length || 0;
    }
  }
  return count;
}

/**
 * Check if a king exists on the board for the given side
 */
function findKingOnBoard(board: any[][], isLight: boolean): boolean {
  for (const row of board) {
    for (const square of row) {
      if (!square.Pieces) continue;
      for (const piece of square.Pieces) {
        if (piece.isKing && piece.isLight === isLight) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Run an entire test suite with AI
 */
export async function runAITestSuite(
  suiteName: string,
  tests: IntegrationTest[],
  config: AITestConfig
): Promise<{ suiteName: string; results: AITestResult[]; passed: number; failed: number; duration: number }> {
  const startTime = performance.now();
  const results: AITestResult[] = [];

  for (const test of tests) {
    results.push(await runAITest(test, config));
  }

  return {
    suiteName,
    results,
    passed: results.filter((r) => r.passed).length,
    failed: results.filter((r) => !r.passed).length,
    duration: performance.now() - startTime,
  };
}
